import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import {
	Page,
	PageContent,
	PageHeaderAddButton,
	PageHeaderListActions,
	ScrollToTopOnMount,
} from "frame/components";
import { canManageOrganisationRecords } from "utils/roles";
import { useRouter } from "utils";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { onList as onStaffListRequested } from "features/staff/actions";
import { HOME_ROUTE, TASK_HOME_ROUTE } from "features/home/home-routes";
import EquipmentUsageListing from "./equipment-usage-listing";
import {
	EQUIPMENT_USAGE_ADD_ROUTE,
	EQUIPMENT_USAGE_EDIT_ROUTE,
	EQUIPMENT_USAGE_LIST_ROUTE,
} from "../actions/types";

const useStyles = makeStyles((theme) => ({
	body: {
		marginTop: theme.spacing(2),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3),
		},
	},
	staffFilter: {
		minWidth: 220,
	},
	muted: {
		color: theme.palette.text.secondary,
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3),
		},
	},
	filterGrid: {
		paddingTop: theme.spacing(1),
	},
	search: {
		display: "flex",
	},
}));

const getCurrentOrganisationId = (currentOrganisation) => {
	if (!currentOrganisation) return "";
	if (typeof currentOrganisation === "string") return currentOrganisation;
	return currentOrganisation.id || "";
};

const mapStaffToOption = (s) => ({
	id: s.id,
	name: `${s.firstName || ""} ${s.lastName || ""}`.trim() || "Unnamed staff",
});

const usageMatchesSearch = (item, rawTerm) => {
	const term = (rawTerm || "").trim().toLowerCase();
	if (!term) return true;
	const haystacks = [
		item.equipmentName,
		item.staffName,
		item.siteName,
		item.taskTitle,
		item.source,
		item.notes,
		item.unitsUsed,
		item.unitType,
	];
	return haystacks.some(
		(value) => value !== null && value !== undefined && String(value).toLowerCase().includes(term)
	);
};

const sortUsage = (items) =>
	[...items].sort((a, b) => {
		const dateA = a.usageDate ? new Date(a.usageDate).getTime() : 0;
		const dateB = b.usageDate ? new Date(b.usageDate).getTime() : 0;
		if (dateB !== dateA) return dateB - dateA;
		return (b.createdUTC || "").localeCompare(a.createdUTC || "");
	});

const EquipmentUsageListPage = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { history } = useRouter();
	const currentOrganisation = useSelector(
		({ organisations }) => organisations.currentOrganisation
	);
	const role = useSelector(({ profile }) => profile.role);
	const staffId = useSelector(({ profile }) => profile.staffId);
	const staffResults = useSelector(({ staff }) => staff.pagination?.results || []);
	const featureFlags = useSelector(({ featureFlags }) => featureFlags);
	const organisationId = getCurrentOrganisationId(currentOrganisation);
	const usageRecordingEnabled =
		featureFlags?.equipment === true &&
		featureFlags?.equipmentUsageRecording === true;
	const viewAllUsage = canManageOrganisationRecords(role);

	const [items, setItems] = useState([]);
	const [selectedStaffId, setSelectedStaffId] = useState("");
	const [loading, setLoading] = useState(false);
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);

	const allStaffOptions = useMemo(
		() => staffResults.map(mapStaffToOption),
		[staffResults]
	);

	const loadUsage = useCallback(async () => {
		if (!organisationId || !usageRecordingEnabled) return;
		setLoading(true);
		try {
			const query = new URLSearchParams();
			const staffFilter = viewAllUsage
				? selectedStaffId || null
				: staffId || null;
			if (staffFilter) {
				query.set("staffId", staffFilter);
			}
			const suffix = query.toString() ? `?${query.toString()}` : "";
			const response = await safetyWiseApi.get(
				`${organisationId}/equipment-usage${suffix}`
			);
			setItems(Array.isArray(response.data) ? response.data : []);
		} catch (e) {
			setItems([]);
		} finally {
			setLoading(false);
		}
	}, [organisationId, selectedStaffId, staffId, usageRecordingEnabled, viewAllUsage]);

	useEffect(() => {
		if (organisationId && viewAllUsage) {
			dispatch(onStaffListRequested());
		}
	}, [dispatch, organisationId, viewAllUsage]);

	useEffect(() => {
		loadUsage();
	}, [loadUsage]);

	const rows = useMemo(() => {
		const sorted = sortUsage(items);
		if (!searchFilter.trim()) return sorted;
		return sorted.filter((item) => usageMatchesSearch(item, searchFilter));
	}, [items, searchFilter]);

	const onSelectUsage = (usage) => {
		if (!usage?.id) return;
		history.push(
			EQUIPMENT_USAGE_EDIT_ROUTE.replace(":usageId", usage.id),
			{ from: EQUIPMENT_USAGE_LIST_ROUTE }
		);
	};

	if (featureFlags && !featureFlags.fetch?.loading && !usageRecordingEnabled) {
		return <Redirect to={HOME_ROUTE} />;
	}

	return (
		<Page title="Equipment Usage">
			<ScrollToTopOnMount />
			<PageHeaderAddButton
				title="Equipment Usage"
				addTabTitle="Record Usage"
				onAdd={() =>
					history.push(EQUIPMENT_USAGE_ADD_ROUTE, {
						from: EQUIPMENT_USAGE_LIST_ROUTE,
					})
				}
				staffUserHide={false}
				backActionPath={TASK_HOME_ROUTE}
				titleAdornment={
					<PageHeaderListActions
						showFilters={showFilters}
						onToggleFilters={() => setShowFilters(!showFilters)}
					/>
				}
			>
				{showFilters && (
					<div className={classes.gridContainer}>
						<Grid container spacing={2} className={classes.filterGrid}>
							{viewAllUsage && (
								<Grid item xs={12} md={6}>
									<FormControl
										variant="outlined"
										size="small"
										className={classes.staffFilter}
										style={{
											backgroundColor: "white",
											maxWidth: 480,
											width: "100%",
										}}
									>
										<InputLabel id="equipment-usage-staff-filter">
											Staff
										</InputLabel>
										<Select
											labelId="equipment-usage-staff-filter"
											value={selectedStaffId}
											onChange={(e) =>
												setSelectedStaffId(e.target.value)
											}
											label="Staff"
										>
											<MenuItem value="">All Staff</MenuItem>
											{allStaffOptions.map((staff) => (
												<MenuItem key={staff.id} value={staff.id}>
													{staff.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
							)}
							<Grid item xs={12} md={6}>
								<TextField
									className={classes.search}
									id="equipment-usage-search"
									variant="outlined"
									label="Search"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
										endAdornment: searchFilter && (
											<InputAdornment position="end">
												<IconButton
													aria-label="clear search"
													onClick={() => setSearchFilter("")}
													edge="end"
													size="small"
												>
													<ClearIcon />
												</IconButton>
											</InputAdornment>
										),
									}}
									value={searchFilter}
									onChange={(e) => setSearchFilter(e.target.value)}
									style={{
										backgroundColor: "white",
										maxWidth: 480,
										width: "100%",
									}}
									placeholder="e.g. Equipment, Staff, Site, Linked, Notes"
								/>
							</Grid>
						</Grid>
					</div>
				)}
			</PageHeaderAddButton>
			<PageContent pad>
				<div className={classes.body}>
					{loading && (
						<Typography className={classes.muted}>
							Loading equipment usage...
						</Typography>
					)}
					{!loading && items.length === 0 && (
						<Typography className={classes.muted}>
							No equipment usage recorded yet.
						</Typography>
					)}
					{!loading && items.length > 0 && rows.length === 0 && (
						<Typography className={classes.muted}>
							No usage records match your search.
						</Typography>
					)}

					{rows.map((item) => (
						<EquipmentUsageListing
							key={item.id}
							usage={item}
							showStaff={viewAllUsage}
							onSelect={onSelectUsage}
						/>
					))}
				</div>
			</PageContent>
		</Page>
	);
};

export default EquipmentUsageListPage;
